import {
  RESERVED_LABEL_NAME_ERROR,
  getReservedLabelValueError,
  isAssignableReservedLabelName,
  isReservedLabelName,
} from '@ipfabric/utils/labels';
import type { FieldError, Merge } from 'react-hook-form';
import { z } from 'zod';
import { normalizeLabelValues } from './labelValueNormalization';
import { labelValueSchema } from './labelValueValidation';

const labelNameSchema = z
  .string()
  .trim()
  .min(1, 'Label name is mandatory.')
  .max(64, 'Label name can contain at most 64 characters.')
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    'Label name can contain only letters, numbers, underscores, and hyphens.',
  )
  .refine((value) => !value.includes(':'), {
    message: 'Label name cannot contain a colon.',
  })
  .superRefine((value, ctx) => {
    if (isAssignableReservedLabelName(value) || !isReservedLabelName(value)) {
      return;
    }
    ctx.addIssue({
      code: 'custom',
      message: RESERVED_LABEL_NAME_ERROR,
    });
  });

type ValuesFieldError =
  | Merge<FieldError, (FieldError | undefined)[]>
  | undefined;

const isFieldError = (value: unknown): value is FieldError =>
  typeof value === 'object' &&
  value !== null &&
  'message' in value &&
  typeof (value as FieldError).message === 'string';

export const getLabelValuesFieldError = (
  valuesError: ValuesFieldError,
): string | undefined => {
  if (!valuesError) {
    return undefined;
  }

  if (valuesError.message) {
    return String(valuesError.message);
  }

  for (const value of Object.values(valuesError)) {
    if (isFieldError(value) && value.message) {
      return String(value.message);
    }
  }

  return undefined;
};

export const labelFormSchema = z
  .object({
    name: labelNameSchema,
    isNoValue: z.boolean(),
    values: z.array(labelValueSchema).default([]),
  })
  .superRefine((data, context) => {
    if (data.isNoValue) {
      // Reserved labels with a value contract (ipf_region) need a value.
      const message = getReservedLabelValueError(data.name, '');
      if (message) {
        context.addIssue({ code: 'custom', message, path: ['values'] });
      }
      return;
    }
    if (data.values.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'At least one possible value is required.',
        path: ['values'],
      });
    }
    for (const [index, value] of data.values.entries()) {
      const message = getReservedLabelValueError(data.name, value);
      if (message) {
        context.addIssue({ code: 'custom', message, path: ['values', index] });
      }
    }
  })
  .transform((data) => ({
    ...data,
    values: data.isNoValue ? [] : normalizeLabelValues(data.values),
  }));

export type LabelFormValues = z.input<typeof labelFormSchema>;
export type LabelFormSubmitValues = z.output<typeof labelFormSchema>;
